import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import md5 from 'md5';
import { useFlowRuleName, useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';

export type CampaignRuleDialogData = {
  rule?: Record<string, unknown>;
  target?: {
    form: {
      flowInline: {
        rules: Record<string, unknown>[];
      };
    };
  };
};

const defaultPath = () => ({
  name: 'Path 1',
  weight: 100,
  status: 'active',
  redirectMode: '302',
  redirectDomain: '',
  directLinking: false,
  landings: [] as unknown[],
  offers: [] as unknown[],
});

const defaultActiveRule = () => ({
  conditions: {} as Record<string, unknown>,
  name: '',
  status: 'active',
  default: false,
  logicalRelation: 'and',
  paths: [defaultPath()],
});

export const useCampaignRuleDialog = (
  getData: () => CampaignRuleDialogData,
  emitClose: () => void,
) => {
  const { t } = useI18n();
  const { buildFlowRuleName } = useFlowRuleName();

  const show = ref(false);
  const loading = ref(false);
  const isLoadingDictionary = ref(false);
  const active_name = ref('');
  const active_rule = ref<Record<string, unknown>>(defaultActiveRule());
  const relation = ref('and');
  const ruleIndex = ref(-1);
  const needToUpdateRuleName = ref(false);
  const oldHash = ref('');
  const target = ref<CampaignRuleDialogData['target']>();

  const getCurrentHash = () => md5(JSON.stringify(active_rule.value));

  const defaultRuleName = computed(() => {
    const { conditions } = active_rule.value;
    const parts = buildFlowRuleName({ conditions: conditions as Record<string, unknown> }, false);

    if (Array.isArray(parts)) {
      return parts
        .map((item) => item.name)
        .join(' | ')
        .substring(0, 80);
    }

    return String(parts).substring(0, 80);
  });

  watch(relation, (value) => {
    active_rule.value.logicalRelation = value;
  });

  const close = () => {
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash()) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const save = () => {
    if (isLoadingDictionary.value) return;
    if (!target.value?.form?.flowInline?.rules) return;

    if (ruleIndex.value === -1) {
      active_rule.value.name = defaultRuleName.value;
      target.value.form.flowInline.rules.push(active_rule.value);
    } else {
      const data = getData();
      if (needToUpdateRuleName.value || data.rule?.name === '') {
        active_rule.value.name = defaultRuleName.value;
      }
      target.value.form.flowInline.rules[ruleIndex.value] = active_rule.value;
    }

    loading.value = false;
    close();
  };

  const onSaveDictionary = (isLoading: boolean) => {
    isLoadingDictionary.value = isLoading;
  };

  const initialize = async () => {
    const data = getData();
    const { rule, target: ruleTarget } = data;
    target.value = ruleTarget;
    relation.value = 'and';

    if (!rule) {
      ruleIndex.value = -1;
      active_rule.value = defaultActiveRule();
      oldHash.value = getCurrentHash();
      show.value = true;
      return;
    }

    ruleIndex.value = -1;
    active_rule.value = { conditions: {} };

    await nextTick();

    if (!target.value?.form?.flowInline?.rules) return;

    ruleIndex.value = target.value.form.flowInline.rules.indexOf(rule);
    active_rule.value = JSON.parse(JSON.stringify(rule));
    oldHash.value = getCurrentHash();
    relation.value = String(active_rule.value.logicalRelation || 'and');
    needToUpdateRuleName.value = data.rule?.name === defaultRuleName.value;
    show.value = true;
  };

  onMounted(() => {
    $emitter.on('loading-add-dictionary', onSaveDictionary);
    initialize();
  });

  onUnmounted(() => {
    $emitter.off('loading-add-dictionary', onSaveDictionary);
  });

  return {
    t,
    show,
    loading,
    active_name,
    active_rule,
    relation,
    checkClose,
    save,
  };
};
